import moment from 'moment-timezone'
import storage from './storage'

import store from '../store'
import DOMPurify from 'dompurify'
import { ALLOWED_ATTR, ALLOWED_TAGS } from '@/constants'
import { isMobile, isTablet } from 'mobile-device-detect'
// TODO: split into different modules

export const clickOutside = {
	bind: function(el, binding, vNode) {
		if (typeof binding.value !== 'function') {
			const compName = vNode.context.name
			let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`

			if (compName) {
				warn += `Found in component '${compName}'`
			}

			console.warn(warn)
		}
		const bubble = binding.modifiers.bubble
		const handler = e => {
			if (bubble || (!el.contains(e.target) && el !== e.target)) {
				binding.value(e)
			}
		}
		el.__vueClickOutside__ = handler

		document.addEventListener('click', handler, true)
	},

	unbind: function(el) {
		document.removeEventListener('click', el.__vueClickOutside__)
		el.__vueClickOutside__ = null
	}
}

export const groupBy = function groupBy(list, keyGetter) {
	const map = new Map()
	list.forEach(item => {
		const key = keyGetter(item)
		const collection = map.get(key)
		if (!collection) {
			map.set(key, [item])
		} else {
			collection.push(item)
		}
	})
	return map
}

export const checkIsAvailableKeyForNumberField = e => {
	if (e.key === null || e.key === ' ') {
		return false
	}
	const key = e.key && e.key.toLowerCase()
	const keyNumber = Number(e.key)
	const isNumber = !isNaN(keyNumber)
	const isBackSpace = key === 'backspace'
	const isTab = key === 'tab'
	const isArrow = key.includes('arrow')

	return isNumber || isBackSpace || isTab || isArrow
}

export const sortTime = timeArr => {
	return timeArr.sort(
		(a, b) =>
			moment(`1999-01-01 ${a.startTime}`) - moment(`1999-01-01 ${b.startTime}`)
	)
}

export const getDateNow = timeZone => {
	if (!timeZone) return null

	return moment().tz(timeZone)
}

export const getCurrentDow = () => {
	const timeZone = store.state.venue.venueInfo.timeZone
	if (!timeZone) return null

	const currentDate = moment().tz(timeZone)

	return {
		currentDow: currentDate.day() + 1,
		currentTime: currentDate.format('HH:mm:ss')
	}
}

export const getTimeNow = timeZone => {
	if (!timeZone) return ''

	const nowDate = getDateNow(timeZone)

	return nowDate ? moment(nowDate).format('HH:mm:ss') : ''
}

export const getAmPmTime = (time, format = 'hh:mm a') => {
	if (!time) return

	return moment(`1999-01-01 ${time}`).format(format)
}

export const getAmPmTimeFromDate = (date, timeZone, format = 'hh:mm a') => {
	return moment(date)
		.tz(timeZone)
		.format(format)
}

export const getFormattedDateTime = (
	date,
	timeZone = 'UTC',
	format = 'YYYY-MM-DD HH:mm:ss.SSSZ'
) => {
	return moment(date)
		.tz(timeZone)
		.format(format)
}

export const getFormattedDate = (date, timeZone, format = 'YYYY-MM-DD') => {
	if (!date || !moment(date).isValid()) return ''

	return timeZone
		? moment(date)
				.tz(timeZone)
				.format(format)
		: moment(date).format(format)
}

export const getFormattedTime = (
	date,
	timeZone,
	format = 'HH:mm:ss',
	formatFrom = 'HH:mm:ss'
) => {
	let dateObject = moment(date)

	if (dateObject.isValid()) {
		return timeZone
			? dateObject.tz(timeZone).format(format)
			: dateObject.format(format)
	}

	dateObject = moment(date, formatFrom)

	if (dateObject.isValid()) {
		return timeZone
			? dateObject.tz(timeZone).format(format)
			: dateObject.format(format)
	}

	return ''
}

export const mergeFormattedDateTime = (date, time, timeZone) => {
	const timeChunks = time.split(':')

	return moment(date)
		.tz(timeZone)
		.hours(timeChunks[0])
		.minutes(timeChunks[1])
		.seconds(0)
		.milliseconds(0)
		.format('YYYY-MM-DD HH:mm:ss.SSSZ')
}

export const addMinutes = (time, minutes) => {
	return moment(time, 'HH:mm:ss')
		.add(minutes, 'minutes')
		.format('HH:mm:ss')
}

export const subtractMinutes = (time, minutes) => {
	return moment(time, 'HH:mm:ss')
		.subtract(minutes, 'minutes')
		.format('HH:mm:ss')
}

export const getTimeDifference = (timeA, timeB) => {
	return moment(timeA, 'HH:mm:ss').diff(moment(timeB, 'HH:mm:ss'))
}

export const getMonth = (date, dateFormat = 'YYYY-MM-DD') => {
	return moment(moment(date, dateFormat).month() + 1, 'M').format('MMMM')
}

export const getYear = (date, dateFormat = 'YYYY-MM-DD') => {
	if (!date || !moment(date, dateFormat).isValid()) return ''

	return `${moment(date, dateFormat).year()}`
}

export const emailValidation = email => {
	//eslint-disable-next-line
	const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	return re.test(String(email).toLowerCase())
}

export const checkCartId = (data, isNeedShowExpiredPopup) => {
	const { getStorage, removeFromStorage, setStorage } = storage
	const { id: cartId } = data
	const storageCartId = getStorage('cartId')

	if (!cartId && storageCartId) {
		removeFromStorage('cartId')

		return
	}

	if (cartId && !storageCartId) {
		setStorage('cartId', cartId)

		return
	}

	if (cartId && storageCartId && cartId !== storageCartId) {
		if (isNeedShowExpiredPopup) {
			store.commit('cart/toggleIsCartRecreated')
		}

		setStorage('cartId', cartId)
	}
}

export const throwError = (message = 'An error occurred') => {
	throw new Error(message)
}

export const formatNumber = (x, digits = 2) => {
	if (isNaN(+x) || x === null) return ''

	let parts = (+x)
		.toFixed(digits)
		.toString()
		.split('.')
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

	return parts.join('.')
}

export const getIsTicketing = () => {
	const { host } = window.location
	return host === process.env.VUE_APP_TICKETS_HOST
	// Optional LAN debug fallback for local network testing:
	// const { host, port } = window.location
	// const isConfiguredTicketingHost = host === process.env.VUE_APP_TICKETS_HOST
	//
	// if (isConfiguredTicketingHost) {
	// 	return true
	// }
	//
	// // In local development we can open ticketing from LAN IP instead of localhost
	// return process.env.NODE_ENV !== 'production' && port === '8088'
}

export const getCleanHTML = (
	html,
	tags = ALLOWED_TAGS,
	attr = ALLOWED_ATTR
) => {
	if (!html) return ''

	const config = { ALLOWED_TAGS: tags, ALLOWED_ATTR: attr }

	return DOMPurify.sanitize(html, config)
}

export const checkIOs = () => {
	return [
		'iPad Simulator',
		'iPhone Simulator',
		'iPod Simulator',
		'iPad',
		'iPhone',
		'iPod'
	].includes(navigator.platform)
}

export const checkMacOS = () => {
	return navigator.userAgent.includes('Mac')
}

export const checkIsChrome = () => {
	const ua = navigator.userAgent.toLowerCase()
	return ua.includes('chrome') || ua.includes('crios')
}

export const isDesktop = () => {
	return !isMobile && !isTablet
}

export const checkAndroidOS = () => {
	const ua = navigator.userAgent.toLowerCase()
	return ua.indexOf('android') > -1
}

export const getWSUrl = () => {
	return process.env.NODE_ENV === 'production'
		? process.env.VUE_APP_WEB_SOCKET_URL
		: 'wss://socket-online-menu-test.smarttab.com'
	// 'wss://socket-online-menu-test-2.smarttab.com'
	// 'wss://socket-online-menu-dev.smarttab.com'
	// 'wss://socket-online-menu-stage.smarttab.com'
}

export const getValidationCode = error => {
	return error?.response?.data?.error?.validationCode || 0
}

export const getCapitalizedWords = text => {
	if (text.includes(' ')) {
		return text
			.split(' ')
			.map(word => getCapitalized(word))
			.join(' ')
	}

	return getCapitalized(text)
}

export const getCapitalized = string => {
	if (!string) return string

	const lowerCaseText = string.toLowerCase()

	return `${lowerCaseText.charAt(0).toUpperCase()}${lowerCaseText.slice(1)}`
}

export const getAddressLink = ({
	addressLine1,
	addressLine2,
	region,
	postalCode,
	city
}) => {
	const parts = [addressLine1, addressLine2, city, region, postalCode]
		.map(s => (s == null ? '' : String(s).trim()))
		.filter(s => s.length > 0)
	const q = encodeURIComponent(parts.join(', '))
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '')
	return isIOS
		? `https://maps.apple.com/?q=${q}`
		: `https://www.google.com/maps/search/?api=1&query=${q}`
}

export const scrollToBottom = () => {
	window?.scrollTo(0, document.body.scrollHeight)
}
